import React, { useState } from 'react';
import { Shapes, Grid, Layers, Download, Copy, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './priorities-ai-design/ProductDesignDocPrimitives';

export const PatternsBackgroundElements: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'patterns' | 'overlays' | 'textures' | 'dividers'>('patterns');
  const [showCode, setShowCode] = useState(false);

  const PatternExample: React.FC<{
    title: string;
    description: string;
    pattern: React.ReactNode;
    usage: string;
    code: string;
  }> = ({ title, description, pattern, usage, code }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
          <p className="text-sm text-[var(--text-sec)]">{description}</p>
        </div>
        <button
          onClick={() => setShowCode(!showCode)}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-[var(--surface-raised)] text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] transition-colors"
        >
          {showCode ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showCode ? 'Hide' : 'Show'} Code
        </button>
      </div>

      <div className="bg-[var(--surface-raised)] p-4 h-32 flex items-center justify-center relative overflow-hidden mb-4">
        {pattern}
      </div>

      <div className="mb-4">
        <p className="text-sm font-semibold text-[var(--text-sec)] mb-2">Usage:</p>
        <p className="text-sm text-[var(--text-sec)]">{usage}</p>
      </div>

      {showCode && (
        <div className="bg-[var(--surface-raised)] p-4">
          <pre className="text-xs text-[var(--text-sec)] overflow-x-auto">
            <code>{code}</code>
          </pre>
        </div>
      )}
    </div>
  );

  const OverlayExample: React.FC<{
    title: string;
    description: string;
    overlay: React.ReactNode;
    usage: string;
    opacity: string;
  }> = ({ title, description, overlay, usage, opacity }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
        <p className="text-sm text-[var(--text-sec)]">{description}</p>
        <p className="text-xs text-[var(--text-ter)] mt-1">Opacity: {opacity}</p>
      </div>

      <div className="bg-[var(--surface-raised)] p-4 h-32 flex items-center justify-center relative overflow-hidden mb-4">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, var(--slate-dim), var(--green-dim))' }}></div>
        <div className="absolute inset-0">
          {overlay}
        </div>
        <div className="relative z-10 text-center">
          <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)]">Content Overlay</h4>
          <p className="text-sm text-[var(--text-sec)]">Example text on pattern</p>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-[var(--text-sec)] mb-2">Usage:</p>
        <p className="text-sm text-[var(--text-sec)]">{usage}</p>
      </div>
    </div>
  );

  const TextureExample: React.FC<{
    title: string;
    description: string;
    texture: React.ReactNode;
    usage: string;
  }> = ({ title, description, texture, usage }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
        <p className="text-sm text-[var(--text-sec)]">{description}</p>
      </div>

      <div className="bg-[var(--surface-raised)] p-4 h-32 flex items-center justify-center relative overflow-hidden mb-4">
        {texture}
      </div>

      <div>
        <p className="text-sm font-semibold text-[var(--text-sec)] mb-2">Usage:</p>
        <p className="text-sm text-[var(--text-sec)]">{usage}</p>
      </div>
    </div>
  );

  const DividerExample: React.FC<{
    title: string;
    description: string;
    divider: React.ReactNode;
    usage: string;
  }> = ({ title, description, divider, usage }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
        <p className="text-sm text-[var(--text-sec)]">{description}</p>
      </div>

      <div className="bg-[var(--surface-raised)] p-4 h-24 flex items-center justify-center relative overflow-hidden mb-4">
        {divider}
      </div>

      <div>
        <p className="text-sm font-semibold text-[var(--text-sec)] mb-2">Usage:</p>
        <p className="text-sm text-[var(--text-sec)]">{usage}</p>
      </div>
    </div>
  );

  return (
    <div>
      <ProductDesignPageHeader
        title="Patterns & Background Elements"
        eyebrow="Texture · overlays"
        backTo="/design-language"
        backLabel="Design language"
        subtitle="Geometric patterns, overlays, textures, and background elements that reinforce the Peak Priorities brand identity and create visual interest across all touchpoints."
      />

      {/* System Overview */}
      <ProductDesignSection title="Background Elements System">
        <p className="text-[var(--text-sec)] leading-relaxed mb-6">
          Our background elements system provides subtle yet powerful visual reinforcement of the Peak Priorities brand.
          These elements should enhance content without overwhelming it, creating depth and visual interest.
        </p>

        <div className="grid md:grid-cols-4 gap-4">
          <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Subtle</h3>
            <p className="text-sm text-[var(--text-sec)]">Background elements should enhance, not distract from content</p>
          </div>
          <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Consistent</h3>
            <p className="text-sm text-[var(--text-sec)]">Use the same patterns across all touchpoints for brand recognition</p>
          </div>
          <div className="border border-[var(--green)] bg-[var(--green-dim)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Purposeful</h3>
            <p className="text-sm text-[var(--text-sec)]">Every element should serve a specific design function</p>
          </div>
          <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Accessible</h3>
            <p className="text-sm text-[var(--text-sec)]">Ensure sufficient contrast and readability over patterns</p>
          </div>
        </div>
      </ProductDesignSection>

      {/* Tab Navigation */}
      <div className="mb-8 flex justify-center gap-4">
        <button
          onClick={() => setActiveTab('patterns')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'patterns'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Shapes className="h-5 w-5" />
          Patterns
        </button>
        <button
          onClick={() => setActiveTab('overlays')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'overlays'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Layers className="h-5 w-5" />
          Overlays
        </button>
        <button
          onClick={() => setActiveTab('textures')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'textures'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Grid className="h-5 w-5" />
          Textures
        </button>
        <button
          onClick={() => setActiveTab('dividers')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'dividers'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <ArrowRight className="h-5 w-5" />
          Dividers
        </button>
      </div>

      {/* Geometric Patterns */}
      {activeTab === 'patterns' && (
        <div>
          <ProductDesignSection title="Geometric Patterns">
            <p className="text-[var(--text-sec)] mb-6">
              Brand-specific geometric patterns that reinforce the navigation and exploration themes of Peak Priorities.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <PatternExample
                title="Compass Grid"
                description="Navigation-inspired grid pattern"
                usage="Background for navigation elements, wayfinding sections"
                code={`<div className="compass-grid-pattern">
  <div className="grid grid-cols-8 gap-2">
    {Array.from({ length: 64 }, (_, i) => (
      <div key={i} className="w-2 h-2 bg-[var(--mustard)] rounded-full opacity-20"></div>
    ))}
  </div>
</div>`}
                pattern={
                  <div className="grid grid-cols-8 gap-2">
                    {Array.from({ length: 64 }, (_, i) => (
                      <div key={i} className="w-2 h-2 bg-[var(--mustard)] rounded-full opacity-20"></div>
                    ))}
                  </div>
                }
              />

              <PatternExample
                title="Mountain Peaks"
                description="Elevation-inspired triangular pattern"
                usage="Background for achievement sections, progress indicators"
                code={`<div className="mountain-peaks-pattern">
  <div className="flex items-end gap-1">
    {Array.from({ length: 12 }, (_, i) => (
      <div
        key={i}
        className="bg-sage-600 opacity-20"
        style={{
          width: '8px',
          height: (Math.random() * 20 + 8) + 'px',
          clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
        }}
      ></div>
    ))}
  </div>
</div>`}
                pattern={
                  <div className="flex items-end gap-1">
                    {Array.from({ length: 12 }, (_, i) => (
                      <div
                        key={i}
                        className="opacity-20 bg-[var(--green)]"
                        style={{
                          width: '8px',
                          height: (Math.random() * 20 + 8) + 'px',
                          clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
                        }}
                      ></div>
                    ))}
                  </div>
                }
              />

              <PatternExample
                title="Target Dots"
                description="Focus-inspired concentric pattern"
                usage="Background for focus sections, goal-oriented content"
                code={`<div className="target-dots-pattern">
  <div className="relative w-16 h-16">
    {Array.from({ length: 3 }, (_, i) => (
      <div
        key={i}
        className="absolute border-2 border-consulting-slate-blue-600 opacity-20 rounded-full"
        style={{
          width: ((i + 1) * 16) + 'px',
          height: ((i + 1) * 16) + 'px',
          top: (8 - (i + 1) * 8) + 'px',
          left: (8 - (i + 1) * 8) + 'px'
        }}
      ></div>
    ))}
  </div>
</div>`}
                pattern={
                  <div className="relative w-16 h-16">
                    {Array.from({ length: 3 }, (_, i) => (
                      <div
                        key={i}
                        className="absolute opacity-20 rounded-full"
                        style={{
                          width: ((i + 1) * 16) + 'px',
                          height: ((i + 1) * 16) + 'px',
                          top: (8 - (i + 1) * 8) + 'px',
                          left: (8 - (i + 1) * 8) + 'px',
                          border: '2px solid var(--slate)'
                        }}
                      ></div>
                    ))}
                  </div>
                }
              />

              <PatternExample
                title="Trail Lines"
                description="Path-inspired connecting lines"
                usage="Background for process flows, journey sections"
                code={`<div className="trail-lines-pattern">
  <svg width="100" height="100" className="opacity-20">
    <path
      d="M10,50 Q30,20 50,50 T90,50"
      stroke="#F59E0B"
      strokeWidth="2"
      fill="none"
    />
    <circle cx="10" cy="50" r="2" fill="#F59E0B" />
    <circle cx="50" cy="50" r="2" fill="#F59E0B" />
    <circle cx="90" cy="50" r="2" fill="#F59E0B" />
  </svg>
</div>`}
                pattern={
                  <svg width="100" height="100" className="opacity-20">
                    <path
                      d="M10,50 Q30,20 50,50 T90,50"
                      stroke="var(--mustard)"
                      strokeWidth="2"
                      fill="none"
                    />
                    <circle cx="10" cy="50" r="2" fill="var(--mustard)" />
                    <circle cx="50" cy="50" r="2" fill="var(--mustard)" />
                    <circle cx="90" cy="50" r="2" fill="var(--mustard)" />
                  </svg>
                }
              />
            </div>
          </ProductDesignSection>

          {/* Pattern Guidelines */}
          <ProductDesignSection title="Pattern Guidelines">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Usage Rules</h3>
                <div className="space-y-4">
                  <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Opacity Control</h4>
                    <p className="text-sm text-[var(--text-sec)]">Keep patterns subtle with 10-30% opacity to avoid overwhelming content</p>
                  </div>

                  <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Color Consistency</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use brand colors only - avoid introducing new colors</p>
                  </div>

                  <div className="border border-[var(--green)] bg-[var(--green-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Scale Appropriately</h4>
                    <p className="text-sm text-[var(--text-sec)]">Adjust pattern size based on content area and viewing distance</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Application Contexts</h3>
                <div className="space-y-4">
                  <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Web Backgrounds</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use for section backgrounds, hero areas, and content dividers</p>
                  </div>

                  <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Presentation Slides</h4>
                    <p className="text-sm text-[var(--text-sec)]">Apply to slide backgrounds and section transitions</p>
                  </div>

                  <div className="border border-[var(--green)] bg-[var(--green-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Document Headers</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use for report covers, proposal headers, and document branding</p>
                  </div>
                </div>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Overlays */}
      {activeTab === 'overlays' && (
        <div>
          <ProductDesignSection title="Overlay Elements">
            <p className="text-[var(--text-sec)] mb-6">
              Subtle overlay patterns that add depth and visual interest without compromising readability.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <OverlayExample
                title="Gradient Overlay"
                description="Subtle gradient overlay for depth"
                usage="Hero sections, background areas, content overlays"
                opacity="20%"
                overlay={
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, var(--mustard-dim), var(--slate-dim))' }}></div>
                }
              />

              <OverlayExample
                title="Dot Overlay"
                description="Subtle dot pattern overlay"
                usage="Content sections, card backgrounds, subtle texture"
                opacity="15%"
                overlay={
                  <div className="absolute inset-0 opacity-15">
                    <div className="grid grid-cols-12 gap-2 h-full">
                      {Array.from({ length: 144 }, (_, i) => (
                        <div key={i} className="w-1 h-1 bg-[var(--green)] rounded-full"></div>
                      ))}
                    </div>
                  </div>
                }
              />

              <OverlayExample
                title="Line Overlay"
                description="Subtle line pattern overlay"
                usage="Section dividers, content backgrounds, structured layouts"
                opacity="10%"
                overlay={
                  <div className="absolute inset-0 opacity-10">
                    <div className="grid grid-cols-8 gap-4 h-full">
                      {Array.from({ length: 8 }, (_, i) => (
                        <div key={i} className="w-px h-full bg-[var(--slate)]"></div>
                      ))}
                    </div>
                  </div>
                }
              />

              <OverlayExample
                title="Geometric Overlay"
                description="Subtle geometric shape overlay"
                usage="Feature sections, special content areas, brand reinforcement"
                opacity="25%"
                overlay={
                  <div className="absolute inset-0 opacity-25">
                    <div className="flex items-center justify-center h-full">
                      <div className="w-16 h-16 rounded-full" style={{ border: '2px solid var(--mustard)' }}></div>
                    </div>
                  </div>
                }
              />
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Textures */}
      {activeTab === 'textures' && (
        <div>
          <ProductDesignSection title="Texture Elements">
            <p className="text-[var(--text-sec)] mb-6">
              Subtle texture elements that add visual interest and depth to backgrounds and surfaces.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <TextureExample
                title="Paper Texture"
                description="Subtle paper-like texture for documents"
                usage="Document backgrounds, report covers, printed materials"
                texture={
                  <div className="w-full h-full relative" style={{ background: 'linear-gradient(135deg, var(--surface), var(--surface-raised))' }}>
                    <div className="absolute inset-0 opacity-10">
                      <div className="grid grid-cols-20 gap-1 h-full">
                        {Array.from({ length: 400 }, (_, i) => (
                          <div key={i} className="w-1 h-1 bg-[var(--text-ter)] rounded-full"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                }
              />

              <TextureExample
                title="Fabric Texture"
                description="Subtle fabric-like texture for surfaces"
                usage="Card backgrounds, surface elements, tactile feel"
                texture={
                  <div className="w-full h-full relative" style={{ background: 'linear-gradient(135deg, var(--surface), var(--surface-raised))' }}>
                    <div className="absolute inset-0 opacity-15">
                      <div className="grid grid-cols-16 gap-2 h-full">
                        {Array.from({ length: 256 }, (_, i) => (
                          <div key={i} className="w-1 h-1 bg-[var(--green)] rounded-full"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                }
              />

              <TextureExample
                title="Metal Texture"
                description="Subtle metallic texture for premium feel"
                usage="Premium content, special features, high-value sections"
                texture={
                  <div className="w-full h-full relative" style={{ background: 'linear-gradient(135deg, var(--surface), var(--surface-raised))' }}>
                    <div className="absolute inset-0 opacity-20">
                      <div className="grid grid-cols-12 gap-3 h-full">
                        {Array.from({ length: 144 }, (_, i) => (
                          <div key={i} className="w-1 h-1 bg-[var(--mustard)] rounded-full"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                }
              />

              <TextureExample
                title="Stone Texture"
                description="Subtle stone-like texture for stability"
                usage="Foundation sections, stable content, reliable features"
                texture={
                  <div className="w-full h-full relative" style={{ background: 'linear-gradient(135deg, var(--surface), var(--surface-raised))' }}>
                    <div className="absolute inset-0 opacity-15">
                      <div className="grid grid-cols-14 gap-2 h-full">
                        {Array.from({ length: 196 }, (_, i) => (
                          <div key={i} className="w-1 h-1 bg-[var(--slate)] rounded-full"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                }
              />
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Section Dividers */}
      {activeTab === 'dividers' && (
        <div>
          <ProductDesignSection title="Section Dividers">
            <p className="text-[var(--text-sec)] mb-6">
              Brand-specific dividers that create visual separation while reinforcing the Peak Priorities identity.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <DividerExample
                title="Compass Divider"
                description="Navigation-inspired section divider"
                usage="Between major sections, navigation elements, wayfinding"
                divider={
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-px bg-[var(--mustard)]"></div>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ border: '2px solid var(--mustard)' }}>
                      <div className="w-1 h-4 bg-[var(--mustard)]"></div>
                    </div>
                    <div className="w-8 h-px bg-[var(--mustard)]"></div>
                  </div>
                }
              />

              <DividerExample
                title="Mountain Divider"
                description="Elevation-inspired section divider"
                usage="Progress sections, achievement areas, milestone markers"
                divider={
                  <div className="flex items-end gap-1">
                    {Array.from({ length: 5 }, (_, i) => (
                      <div
                        key={i}
                        className="bg-[var(--green)]"
                        style={{
                          width: '8px',
                          height: ((i + 1) * 4) + 'px',
                          clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
                        }}
                      ></div>
                    ))}
                  </div>
                }
              />

              <DividerExample
                title="Target Divider"
                description="Focus-inspired section divider"
                usage="Goal sections, focus areas, priority content"
                divider={
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full" style={{ border: '2px solid var(--slate)' }}></div>
                    <div className="w-6 h-4 rounded-full" style={{ border: '2px solid var(--slate)' }}></div>
                    <div className="w-8 h-4 rounded-full" style={{ border: '2px solid var(--slate)' }}></div>
                  </div>
                }
              />

              <DividerExample
                title="Trail Divider"
                description="Path-inspired section divider"
                usage="Process flows, journey sections, step-by-step content"
                divider={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[var(--mustard)] rounded-full"></div>
                    <div className="w-4 h-px bg-[var(--mustard)]"></div>
                    <div className="w-2 h-2 bg-[var(--mustard)] rounded-full"></div>
                    <div className="w-4 h-px bg-[var(--mustard)]"></div>
                    <div className="w-2 h-2 bg-[var(--mustard)] rounded-full"></div>
                  </div>
                }
              />
            </div>
          </ProductDesignSection>

          {/* Divider Guidelines */}
          <ProductDesignSection title="Divider Guidelines">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Usage Rules</h3>
                <div className="space-y-4">
                  <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Consistent Spacing</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use consistent spacing above and below dividers</p>
                  </div>

                  <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Brand Colors Only</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use brand colors exclusively for dividers</p>
                  </div>

                  <div className="border border-[var(--green)] bg-[var(--green-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Appropriate Scale</h4>
                    <p className="text-sm text-[var(--text-sec)]">Scale dividers appropriately for their context</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Application Contexts</h3>
                <div className="space-y-4">
                  <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Web Sections</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use between major content sections and navigation areas</p>
                  </div>

                  <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Document Layouts</h4>
                    <p className="text-sm text-[var(--text-sec)]">Apply to report sections, proposal chapters, and document divisions</p>
                  </div>

                  <div className="border border-[var(--green)] bg-[var(--green-dim)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Presentation Slides</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use for slide transitions and section breaks</p>
                  </div>
                </div>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Implementation Guidelines */}
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-8 text-center">
        <h3 className="text-2xl font-heading font-semibold text-[var(--text-pri)] mb-3">
          Implementation Guidelines
        </h3>
        <p className="text-[var(--text-sec)] max-w-3xl mx-auto mb-6">
          Use these patterns and background elements consistently across all Peak Priorities touchpoints.
          They should enhance content without overwhelming it, creating visual interest and brand recognition.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--slate)] text-white font-semibold transition-colors hover:opacity-90">
            <Download className="h-5 w-5" />
            Download Assets
          </button>
          <button className="flex items-center gap-2 px-6 py-3 border border-[var(--rule)] bg-[var(--surface)] text-[var(--text-pri)] font-semibold transition-colors hover:bg-[var(--hover-tint)]">
            <Copy className="h-5 w-5" />
            Copy Code
          </button>
        </div>
      </div>
    </div>
  );
};
